import { useCallback, useEffect, useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import { PAGES, PAGE_GROUPS, formatDateTime } from '@nu/shared';
import { api, ApiError } from '../api/client.js';

// Platform > Permissions > Page access: pick a department, then a page, then
// set each person's access to it. Everyone starts on their role's default
// (office staff can edit, for most pages); a change here applies to that
// person straight away and is kept in the Audit trail.
// As the Sign-in access tab names them.
const SIGN_IN = { field: 'Field', office: 'Office', both: 'Field + office', admin: 'Master admin' };

function levelText(page, level) {
  if (level === 'none') return 'No access';
  // A page with one level reads as "Has access", whichever level it is.
  if (page.levels.length === 1) return 'Has access';
  if (level === 'view') return 'Can view';
  return 'Can edit';
}

// The colour for what someone actually gets: green can edit (or has a
// one-level page), yellow can view, grey no access.
function tone(page, level) {
  if (level === 'none') return 'none';
  if (page.levels.length === 1 || level === 'edit') return 'edit';
  return 'view';
}

function problem(e) {
  if (e?.code === 'offline') return 'No connection. Permissions can only be changed online.';
  if (e instanceof ApiError) return e.message;
  return 'Something went wrong. Try again.';
}

export default function PageAccess({ me }) {
  const [groupId, setGroupId] = useState(PAGE_GROUPS[0].id);
  const pages = PAGES.filter((p) => p.group === groupId);
  const [pageId, setPageId] = useState(pages[0].id);
  const page = PAGES.find((p) => p.id === pageId) || pages[0];
  const [people, setPeople] = useState(null);
  const [error, setError] = useState(null);
  const [q, setQ] = useState('');
  const [onlyWith, setOnlyWith] = useState(false);
  const [saving, setSaving] = useState({});
  const [notice, setNotice] = useState(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setPeople((await api(`/permissions/pages/${page.id}`)).people);
    } catch (e) {
      setError(problem(e));
    }
  }, [page.id]);

  useEffect(() => { setPeople(null); setNotice(null); load(); }, [load]);

  const pickGroup = (id) => {
    setGroupId(id);
    setPageId(PAGES.find((p) => p.group === id).id);
  };

  const shown = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (people || []).filter((p) => (!onlyWith || p.level !== 'none')
      && (!s || p.name.toLowerCase().includes(s) || p.email.toLowerCase().includes(s)));
  }, [people, q, onlyWith]);
  const withAccess = (people || []).filter((p) => p.level !== 'none').length;

  // Ticked people, to set them all at once. Cleared when the page changes.
  const [picked, setPicked] = useState(() => new Set());
  const [bulkLevel, setBulkLevel] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);
  useEffect(() => { setPicked(new Set()); setBulkLevel(''); }, [page.id]);
  const pickable = shown.filter((p) => p.canHave);
  const allPicked = pickable.length > 0 && pickable.every((p) => picked.has(p.id));
  const togglePick = (id) => setPicked((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const applyBulk = async () => {
    const list = (people || []).filter((p) => picked.has(p.id) && p.canHave);
    if (!list.length || !bulkLevel) return;
    setBulkBusy(true);
    setNotice(null);
    const failed = [];
    for (const p of list) {
      try {
        await api(`/permissions/pages/${page.id}/people/${p.id}`, { method: 'PUT', body: { level: bulkLevel } });
      } catch (e) {
        failed.push(`${p.name} (${problem(e)})`);
      }
    }
    const done = list.length - failed.length;
    const what = bulkLevel === 'default' ? 'back to the default' : levelText(page, bulkLevel).toLowerCase();
    setNotice(failed.length
      ? { tone: 'bad', text: `${done} updated; not changed: ${failed.join(', ')}.` }
      : { tone: 'ok', text: `${page.label}: ${done} ${done === 1 ? 'person' : 'people'} set to ${what}.` });
    setPicked(new Set());
    setBulkLevel('');
    setBulkBusy(false);
    await load();
  };

  const set = async (person, level) => {
    setSaving((s) => ({ ...s, [person.id]: true }));
    setNotice(null);
    try {
      await api(`/permissions/pages/${page.id}/people/${person.id}`, { method: 'PUT', body: { level } });
      const now = level === 'default' ? `back to the default (${levelText(page, person.default)})` : levelText(page, level).toLowerCase();
      setNotice({ tone: 'ok', text: `${person.name}: ${page.label}, ${now}.` });
      await load();
    } catch (e) {
      setNotice({ tone: 'bad', text: problem(e) });
    } finally {
      setSaving((s) => ({ ...s, [person.id]: false }));
    }
  };

  return (
    <>
      <div className="perm-step">
        <span className="small muted">Department</span>
        <div className="ticket-views" role="tablist" aria-label="Department">
          {PAGE_GROUPS.map((g) => (
            <button key={g.id} type="button" role="tab" aria-selected={groupId === g.id} className={`chip${groupId === g.id ? ' on' : ''}`} onClick={() => pickGroup(g.id)}>{g.label}</button>
          ))}
        </div>
      </div>
      <div className="perm-step">
        <span className="small muted">Page</span>
        <div className="ticket-views" role="tablist" aria-label="Page">
          {pages.map((p) => (
            <button key={p.id} type="button" role="tab" aria-selected={page.id === p.id} className={`chip${page.id === p.id ? ' on' : ''}`} onClick={() => setPageId(p.id)}>{p.label}</button>
          ))}
        </div>
      </div>

      <h2 className="perm-title">{page.label}</h2>
      <p className="muted small">
        {page.levels.includes('view')
          ? 'Can view: open the page and look. Can edit: also make changes.'
          : 'One level: with access, a person can do everything on this page.'}
        {' '}{Object.keys(page.defaults).length ? 'Only master admins have it by default; everyone else is given it here.' : 'Nobody has it until they’re given it.'} Changes apply straight away.
        {page.app && ' People with the phone app only are listed too: this page is also a screen in their app.'}
      </p>
      {notice && <p className={`notice-line notice-${notice.tone}`} role="status">{notice.text}</p>}

      <div className="perm-tools">
        <label className="search">
          <Search size={18} aria-hidden="true" />
          <input type="search" placeholder="Filter by name or email" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Filter people" />
        </label>
        <label className="check small">
          <input type="checkbox" checked={onlyWith} onChange={(e) => setOnlyWith(e.target.checked)} /> Only people with access
        </label>
        {people && <span className="small muted">{withAccess} of {people.length} have access</span>}
      </div>

      {error && (
        <div className="empty">
          <p>{error}</p>
          <button type="button" className="btn" onClick={load}>Try again</button>
        </div>
      )}
      {!error && !people && <p className="muted">Loading…</p>}
      {people && shown.length === 0 && <p className="muted">{people.length ? 'Nobody matches that filter.' : 'Nobody has office access yet.'}</p>}

      {picked.size > 0 && (
        <div className="perm-bulk" role="region" aria-label="Change the ticked people">
          <strong>{picked.size} selected</strong>
          <select value={bulkLevel} onChange={(e) => setBulkLevel(e.target.value)} aria-label="Access for the ticked people">
            <option value="">Set {page.label} to…</option>
            <option value="none">No access</option>
            {page.levels.includes('view') && <option value="view">{levelText(page, 'view')}</option>}
            {page.levels.includes('edit') && <option value="edit">{levelText(page, 'edit')}</option>}
            <option value="default">Default</option>
          </select>
          <button type="button" className="btn primary" disabled={!bulkLevel || bulkBusy} onClick={applyBulk}>{bulkBusy ? 'Saving…' : 'Apply'}</button>
          <button type="button" className="link-btn" disabled={bulkBusy} onClick={() => setPicked(new Set())}>Clear</button>
        </div>
      )}

      {shown.length > 0 && (
        <div className="table-scroll">
          <table className="table">
            <thead>
              <tr>
                <th className="perm-pick"><input type="checkbox" checked={allPicked} disabled={!pickable.length} aria-label="Tick everyone shown" onChange={() => setPicked(allPicked ? new Set() : new Set(pickable.map((p) => p.id)))} /></th>
                <th>Name</th><th>Signs in as</th><th>Access to {page.label}</th><th>Last changed</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((p) => (
                <tr key={p.id} className={picked.has(p.id) ? 'is-picked' : ''}>
                  <td className="perm-pick">
                    {p.canHave && <input type="checkbox" checked={picked.has(p.id)} onChange={() => togglePick(p.id)} aria-label={`Tick ${p.name}`} />}
                  </td>
                  <td data-label="Name">
                    <strong>{p.name}</strong>{p.id === me.id && <span className="tag">You</span>}
                    <div className="muted small">{p.email}</div>
                  </td>
                  <td data-label="Signs in as">{p.signIn ? SIGN_IN[p.signIn] : <span className="warn-text">No access any more</span>}</td>
                  <td data-label="Access">
                    {p.canHave ? (
                      <select className={`access-select access-${tone(page, p.level)}`} value={p.set ?? 'default'} disabled={saving[p.id] || bulkBusy} onChange={(e) => set(p, e.target.value)} aria-label={`${page.label} access for ${p.name}`}>
                        <option value="default">Default ({levelText(page, p.default)})</option>
                        <option value="none">No access</option>
                        {page.levels.includes('view') && <option value="view">Can view</option>}
                        {page.levels.includes('edit') && <option value="edit">{levelText(page, 'edit')}</option>}
                      </select>
                    ) : (
                      <>
                        <span className="muted">No office access</span>
                        {p.set && <div><button type="button" className="link-btn" disabled={saving[p.id]} onClick={() => set(p, 'default')}>Clear setting</button></div>}
                      </>
                    )}
                  </td>
                  <td data-label="Last changed" className="small muted">{p.setAt ? <>{p.setBy}<br />{formatDateTime(p.setAt)}</> : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
